import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { useAuth } from '../context/AuthContext'
import { useCurrency } from '../context/CurrencyContext'
import { adminAPI, kycAPI, propertiesAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { toast } from 'sonner'
import { Users, Building2, TrendingUp, Shield, Loader2, CheckCircle2, XCircle, Eye, Coins, Clock } from 'lucide-react'

const statusColors = {
  pending: 'bg-slate-100 text-slate-600', in_review: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700', draft: 'bg-slate-100 text-slate-600', pending_review: 'bg-amber-100 text-amber-700',
  tokenized: 'bg-purple-100 text-purple-700', active: 'bg-emerald-100 text-emerald-700',
}

export function AdminDashboard() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isAdmin } = useAuth()
  const { formatPrice } = useCurrency()
  const [stats, setStats] = useState(null)
  const [pendingKYC, setPendingKYC] = useState([])
  const [pendingProperties, setPendingProperties] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) { navigate('/'); return }
    loadData()
  }, [isAuthenticated, isAdmin, navigate])

  const loadData = async () => {
    try {
      const [statsRes, kycRes, propsRes, usersRes] = await Promise.all([
        adminAPI.getStats(), kycAPI.getPending(), propertiesAPI.list({ status: 'pending_review' }), adminAPI.listUsers({ limit: 20 })
      ])
      setStats(statsRes.data); setPendingKYC(kycRes.data); setPendingProperties(propsRes.data); setUsers(usersRes.data)
    } catch (error) { console.error('Failed:', error) }
    finally { setLoading(false) }
  }

  const handleKYCApprove = async (id) => { try { await kycAPI.approve(id); toast.success('KYC approved!'); loadData() } catch { toast.error('Failed') } }
  const handleKYCReject = async (id) => { try { await kycAPI.reject(id, 'Documents need verification'); toast.success('KYC rejected'); loadData() } catch { toast.error('Failed') } }
  const handlePropertyApprove = async (id) => { try { await propertiesAPI.approve(id); toast.success('Property approved!'); loadData() } catch { toast.error('Failed') } }

  if (loading) return (<div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex items-center justify-center pt-32"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div></div>)

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="text-3xl font-heading text-brand-secondary">{t('dashboard.admin.title')}</h1>
            <p className="text-slate-500 mt-1">{t('dashboard.admin.subtitle')}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[
              { label: t('dashboard.admin.users'), value: stats?.users_count || 0, icon: Users, color: 'text-blue-600' },
              { label: t('dashboard.admin.properties'), value: stats?.properties_count || 0, icon: Building2, color: 'text-brand-primary' },
              { label: t('dashboard.admin.activeOfferings'), value: stats?.active_offerings || 0, icon: TrendingUp, color: 'text-emerald-600' },
              { label: t('dashboard.admin.pendingKyc'), value: stats?.pending_kyc || 0, icon: Shield, color: 'text-amber-600' },
              { label: t('dashboard.admin.totalRaised'), value: formatPrice(stats?.total_raised || 0), icon: Coins, color: 'text-emerald-600' },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-slate-100 p-5">
                <div className="flex items-center justify-between mb-3"><span className="text-slate-500 text-sm uppercase tracking-wider">{stat.label}</span><stat.icon className={`w-5 h-5 ${stat.color}`} /></div>
                <p className="text-2xl font-heading text-brand-secondary font-mono" data-testid={`admin-stat-${i}`}>{stat.value}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Tabs defaultValue="kyc">
              <TabsList className="bg-white border border-slate-100 p-1 rounded-none">
                <TabsTrigger value="kyc" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="kyc-tab"><Shield className="w-4 h-4 mr-2" /> {t('dashboard.admin.kycReviews')} ({pendingKYC.length})</TabsTrigger>
                <TabsTrigger value="properties" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="properties-tab"><Building2 className="w-4 h-4 mr-2" /> {t('dashboard.admin.properties')} ({pendingProperties.length})</TabsTrigger>
                <TabsTrigger value="users" className="data-[state=active]:bg-brand-primary/10 data-[state=active]:text-brand-secondary text-slate-500 rounded-none font-semibold" data-testid="users-tab"><Users className="w-4 h-4 mr-2" /> {t('dashboard.admin.users')}</TabsTrigger>
              </TabsList>

              <TabsContent value="kyc" className="mt-6">
                <div className="bg-white border border-slate-100">
                  {pendingKYC.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {pendingKYC.map((kyc) => (
                        <div key={kyc.id} className="p-5 flex items-center justify-between" data-testid={`kyc-row-${kyc.id}`}>
                          <div className="flex items-center gap-4"><div className="w-10 h-10 bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div><div><p className="text-brand-secondary font-semibold">{kyc.full_name}</p><p className="text-sm text-slate-400">{kyc.nationality} • {kyc.document_type}</p></div></div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => handleKYCApprove(kyc.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-none" data-testid={`approve-kyc-${kyc.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> {t('dashboard.admin.approve')}</Button>
                            <Button size="sm" variant="outline" onClick={() => handleKYCReject(kyc.id)} className="border-red-300 text-red-600 hover:bg-red-50 rounded-none" data-testid={`reject-kyc-${kyc.id}`}><XCircle className="w-4 h-4 mr-1" /> {t('dashboard.admin.reject')}</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (<div className="p-12 text-center"><CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-4" /><p className="text-slate-500">{t('dashboard.admin.allReviewed')}</p></div>)}
                </div>
              </TabsContent>

              <TabsContent value="properties" className="mt-6">
                <div className="bg-white border border-slate-100">
                  {pendingProperties.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {pendingProperties.map((property) => (
                        <div key={property.id} className="p-5 flex items-center justify-between" data-testid={`property-review-${property.id}`}>
                          <div className="flex items-center gap-4"><div className="w-16 h-16 bg-slate-100 overflow-hidden"><img src={property.images?.[0] || 'https://images.unsplash.com/photo-1760611655987-d348d6d28174?w=200&q=80'} alt={property.name} className="w-full h-full object-cover" /></div>
                            <div><p className="text-brand-secondary font-semibold">{property.name}</p><p className="text-sm text-slate-400">{property.city}, {property.country} • {formatPrice(property.total_value)}</p></div></div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/property/${property.id}`)} className="text-slate-500 hover:text-brand-secondary rounded-none"><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" onClick={() => handlePropertyApprove(property.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-none" data-testid={`approve-property-${property.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> {t('dashboard.admin.approve')}</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (<div className="p-12 text-center"><CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-4" /><p className="text-slate-500">{t('dashboard.admin.noPendingProperties')}</p></div>)}
                </div>
              </TabsContent>

              <TabsContent value="users" className="mt-6">
                <div className="bg-white border border-slate-100 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#F8FAFC]">
                      <tr>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">{t('dashboard.admin.user')}</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">{t('dashboard.admin.role')}</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">KYC</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">{t('dashboard.admin.wallet')}</th>
                        <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">{t('dashboard.admin.joined')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-[#F8FAFC]" data-testid={`user-row-${u.id}`}>
                          <td className="p-4"><div><p className="text-brand-secondary font-semibold">{u.name}</p><p className="text-sm text-slate-400">{u.email || 'Wallet user'}</p></div></td>
                          <td className="p-4"><span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${u.role === 'admin' ? 'bg-red-100 text-red-700' : u.role === 'issuer' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{u.role}</span></td>
                          <td className="p-4"><span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${statusColors[u.kyc_status]}`}>{u.kyc_status}</span></td>
                          <td className="p-4">{u.wallet_address ? <span className="text-sm text-slate-500 font-mono">{u.wallet_address.slice(0, 6)}...{u.wallet_address.slice(-4)}</span> : <span className="text-slate-300">-</span>}</td>
                          <td className="p-4 text-sm text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </main>
    </div>
  )
}
